import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useUserData } from "components/customHooks/userDataContext";
import {
  addPracticesToUser,
  removePracticesFromUser,
  updateScribeProviderAccess,
} from "apiHandler/actions";

const PracticeList = ({
  localPracticeList,
  selectedUser,
  setLocalPracticeList,
  setHasChanges,
  setSnack,
}) => {
  const [selectedPracticeIds, setSelectedPracticeIds] = useState([]);
  const [showPracticeEdit, setShowPracticeEdit] = useState(false);

  const userData = useUserData();
  const token = userData?.access_token;

  const handleAddPractices = async () => {
    if (!selectedPracticeIds.length) return;
    const payload = {
      user_id: selectedUser.user_id,
      user_name: selectedUser.user_name,
      practice_id_list: selectedPracticeIds.map((p) => p.practice_id),
    };
    try {
      const res = await addPracticesToUser(payload, token);
      if (res?.status === "success") {
        setLocalPracticeList((prev) => [
          ...prev,
          ...selectedPracticeIds.filter(
            (p) => !prev.some((prevP) => prevP.practice_id === p.practice_id)
          ),
        ]);
        setSelectedPracticeIds([]);
        setHasChanges(true);
      }
    } catch (error) {
      console.log("Add practices error:", error);
    }
  };

  const handleRemovePractice = async (practiceId) => {
    const payload = {
      user_id: selectedUser.user_id,
      user_name: selectedUser.user_name,
      practice_id_list: [practiceId],
    };

    try {
      const res = await removePracticesFromUser(payload, token);
      if (res?.status === "success") {
        setLocalPracticeList((prev) =>
          prev.filter((p) => String(p.practice_id) !== String(practiceId))
        );
        setHasChanges(true);
      }
    } catch (error) {
      console.log("Remove single practice error:", error);
    }
  };

  const addRemoveProvider = async (npi, action) => {
    const payload = {
      user_id: selectedUser.user_id,
      npi,
      action,
    };

    try {
      const res = await updateScribeProviderAccess(payload, token);
      if (res?.status === "success") {
        setHasChanges(true);

        const updatedProviderList = selectedUser.provider_list.map((prov) => {
          if (prov.npi === npi) {
            return {
              ...prov,
              scribe_provider_access: action === "add" ? "Yes" : "No",
            };
          }
          return prov;
        });

        selectedUser.provider_list = updatedProviderList;

        setLocalPracticeList((prev) => [...prev]);
      }
    } catch (error) {
      console.log("Update provider access error:", error);
      setSnack({
        open: true,
        message: "Failed to update provider access. Please try again.",
        severity: "error",
      });
    }
  };
  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant="subtitle2" color="textSecondary">
          Practice List
        </Typography>
        <IconButton
          size="small"
          onClick={() => setShowPracticeEdit((prev) => !prev)}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>

      {showPracticeEdit && (
        <>
          <Autocomplete
            multiple
            options={(userData?.practices || []).filter(
              (option) =>
                !localPracticeList.some(
                  (p) => p.practice_id === option.practice_id
                )
            )}
            getOptionLabel={(option) =>
              `${option.practice_name} (${option.practice_id})`
            }
            isOptionEqualToValue={(option, value) =>
              option.practice_id === value.practice_id
            }
            value={selectedPracticeIds}
            onChange={(_, value) => setSelectedPracticeIds(value)}
            renderInput={(params) => (
              <TextField {...params} label="Select Practices" size="small" />
            )}
          />

          <Box mt={1}>
            <Button variant="contained" onClick={handleAddPractices}>
              Add
            </Button>
          </Box>
        </>
      )}

      <Box mb={1}>
        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr auto"
          gap={2}
          sx={{
            px: 1,
            fontWeight: 600,
            borderBottom: "2px solid #ccc",
            pb: 1,
          }}
        >
          <Typography variant="subtitle2">Practice Name</Typography>
          <Typography variant="subtitle2">Practice ID</Typography>
          <Typography variant="subtitle2" />
        </Box>

        {localPracticeList.map((practice) => {
          const providers =
            selectedUser?.provider_list?.filter(
              (prov) =>
                String(prov.practice_id) === String(practice.practice_id)
            ) || [];

          return (
            <Box key={practice.practice_id}>
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr auto"
                gap={2}
                alignItems="center"
                py={1}
                px={1}
                sx={{ borderBottom: "1px solid #eee" }}
              >
                <Typography variant="body2">
                  {practice.practice_name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {practice.practice_id}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleRemovePractice(practice.practice_id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              {providers.length > 0 && (
                <Box
                  px={3}
                  py={1}
                  sx={{
                    backgroundColor: "#fafafa",
                    borderLeft: "3px solid #1976d2",
                  }}
                >
                  <Box
                    display="grid"
                    gridTemplateColumns="1fr 1fr 100px"
                    gap={2}
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    <Typography variant="subtitle2">Provider Name</Typography>
                    <Typography variant="subtitle2">NPI</Typography>
                    <Typography variant="subtitle2">Access</Typography>
                  </Box>

                  {providers.map((provider, idx) => {
                    const fullName = `${provider.first_name} ${provider.last_name}`;
                    const hasAccess = provider.scribe_provider_access === "Yes";

                    return (
                      <Box
                        key={idx}
                        display="grid"
                        gridTemplateColumns="1fr 1fr 100px"
                        gap={2}
                        alignItems="center"
                        py={0.5}
                        sx={{ borderBottom: "1px dashed #ccc" }}
                      >
                        <Typography variant="body2">{fullName}</Typography>
                        <Typography variant="body2">{provider.npi}</Typography>
                        <Checkbox
                          checked={hasAccess}
                          onChange={async () => {
                            const newAccess = hasAccess ? "No" : "Yes";
                            provider.scribe_provider_access = newAccess;

                            await addRemoveProvider(
                              provider.npi,
                              newAccess === "Yes" ? "add" : "remove"
                            );
                          }}
                          size="small"
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default PracticeList;
